import _merge from 'lodash/merge';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { Lightning } from '@lightningjs/sdk';
import { templates, sizes, imageOptions } from './square.constants';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { getInitialStyle, getFocusedStyle, defaultContext, IMAGE_OFFSET, unFocusedStyle } from './square.style';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import { areContentItemsEqual, areContextsEqual } from '../utils/templates';

/**
 * Square is a component with icon centered inside
 *
 * @see https://app.abstract.com/projects/162522cb-a770-4688-9ead-06e1fd627b20/branches/master/collections/def79ce0-c974-417e-b7a2-85491afde8e8
 */
export default class Square extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _attach() {
    const image = this.tag('Image');

    image.on('txLoaded', () => {
      image.setSmooth('alpha', 1);
    });
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
    this.tag('Image').removeAllListeners('txLoaded');
  }

  set item({ content, config, renderingContext = RENDERING_CONTEXT.GENERAL }) {
    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;

      return;
    }

    this._item = content;
    this._config = config || sizes[templates.Square][renderingContext][this._style.screenSize];
    this._renderingContext = renderingContext;

    const imageUrl = (content.image && content.image.src) || '';
    const offset = IMAGE_OFFSET[this._style.screenSize];

    this.patch({
      w: this._config.w,
      h: this._config.h,
      Border: {
        w: this._config.w + offset,
        h: this._config.h + offset,
      },
      Image: {
        w: this._config.w,
        h: this._config.h,
        src: imageUrl,
        alpha: 0.0001,
      },
    });
  }

  get item() {
    return this._item;
  }

  _focus() {
    this.patch(getFocusedStyle({ colorMode: this._style.colorMode }));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Square.Templates = templates;
Square.Sizes = sizes;
Square.ImageOptions = imageOptions;
